/**
 * Datas nos formatos que o canvas usa:
 *   "19 jul 2026"          — cabeçalho do detalhe da volta
 *   "19 jul"               — mesmo ano, linhas compactas
 *   "hoje, 07:20"          — volta em andamento começada hoje
 *
 * **Dia primeiro nos dois idiomas.** O mês é palavra, então a ordem é inequívoca, e assim não é
 * preciso escolher entre variantes regionais do inglês.
 */

import { t } from '@/lib/i18n';

/** Uma tabela, não `Intl.DateTimeFormat` — ver o comentário em `messages/format.ts`. */
export const MONTHS: readonly string[] = t('format.months').split(' ');

function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function month(d: Date): string {
  return MONTHS[d.getMonth()] ?? '';
}

/** "jul 2026" — o mês de um marco, quando o dia não importa (canvas 3a: "Melhor volta · jul 2026"). */
export function fmtMonthYear(value: string | Date): string {
  const d = toDate(value);
  return `${month(d)} ${d.getFullYear()}`;
}

/** "19 jul 2026" */
export function fmtDateLong(value: string | Date): string {
  const d = toDate(value);
  return `${d.getDate()} ${month(d)} ${d.getFullYear()}`;
}

/** "19 jul", or "19 jul 2025" when the year differs from now. */
export function fmtDateShort(value: string | Date, now = new Date()): string {
  const d = toDate(value);
  const base = `${d.getDate()} ${month(d)}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base} ${d.getFullYear()}`;
}

/** "07:20" */
export function fmtTime(value: string | Date): string {
  const d = toDate(value);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Como o canvas rotula uma volta em andamento: "hoje, 07:20" quando começou hoje, senão só a
 * data. Ontem ganha palavra própria porque é assim que alguém pensa ao decidir se vai terminar
 * aquela volta.
 */
export function fmtStartedAt(value: string | Date, now = new Date()): string {
  const d = toDate(value);
  if (isSameDay(d, now)) return t('format.today', { time: fmtTime(d) });

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(d, yesterday)) return t('format.yesterday', { time: fmtTime(d) });

  return fmtDateShort(d, now);
}

/** "19 jul, 18:04" — used for "handicap atualizado em". */
export function fmtDateTime(value: string | Date, now = new Date()): string {
  return `${fmtDateShort(value, now)}, ${fmtTime(value)}`;
}

/** Machine-readable value for a <time dateTime=""> attribute. */
export function isoDate(value: string | Date): string {
  return toDate(value).toISOString();
}
